import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { fetchProductPrices } from '../../utils/api';
import { useTheme } from '../../context/ThemeContext';
import { RETAILER_LOGOS } from '../../config/retailers';
import { findProductById } from '../../data/productData';
import './SimplePriceComparison.css'; // Add CSS import
import { FaLink, FaQrcode, FaSms, FaFacebookF, FaTwitter } from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import { FaAmazon, FaExternalLinkAlt } from "react-icons/fa";
import { IoMdPricetag } from 'react-icons/io';
import { MdContentCopy, MdCheck, MdShare, MdQrCode2 } from "react-icons/md";
import { FaXTwitter, FaFacebook, FaWhatsapp } from "react-icons/fa6";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PriceFeedbackForm from './PriceFeedbackForm';

// CSS is defined inline for simplicity
const SimplePriceComparison = ({ specificProductId, csvLookupId, csvExactMatchOnly = false }) => {
  const { productId: urlProductId } = useParams();
  const productId = specificProductId || urlProductId;
  const { theme } = useTheme();
  
  // Get product details from any data file
  const productDetails = findProductById('transducer', productId) || 
                        findProductById('screen', productId) || 
                        findProductById('battery', productId) || 
                        findProductById('shuttle', productId) || 
                        findProductById('pole', productId);
  
  const [productData, setProductData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  
  // Add new states for enhanced loading experience
  const [loadingDuration, setLoadingDuration] = useState(0);
  const [silentRetry, setSilentRetry] = useState(false);
  const loadingTimerRef = useRef(null);
  const retryTimerRef = useRef(null);
  
  // Add a new state to track retry attempts
  const [retryAttempts, setRetryAttempts] = useState(0);
  const MAX_RETRY_ATTEMPTS = 3; // Maximum number of retries before showing "No data found"

  // Check if debug mode is enabled via URL param
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const debug = queryParams.get('debug') === 'true';
    setShowDebug(debug);
    
    if (debug) {
      console.log('Debug mode enabled for PriceComparison');
    }
  }, []);

  // Check if device is mobile or desktop
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      const mobile = width <= 768;
      const desktop = width >= 1024;
      
      setIsMobile(mobile);
      setIsDesktop(desktop);
      
      console.log(`Window width: ${width}px, isMobile: ${mobile}, isDesktop: ${desktop}`);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const fetchPriceData = async () => {
      try {
        if (!silentRetry) {
          setLoading(true);
        }
        console.log('Fetching price data for product:', productId);
        if (csvLookupId) {
          console.log('Using exact CSV lookup ID for matching:', csvLookupId);
          if (csvExactMatchOnly) {
            console.log('EXACT MATCH ONLY MODE: Will only return exact matches for', csvLookupId);
          }
        }
        
        // Track the time for performance debugging
        const startTime = performance.now();
        
        // Pass the csvExactMatchOnly flag to the API
        const result = await fetchProductPrices(
          productId, 
          0, 
          3, 
          csvLookupId, 
          csvExactMatchOnly
        );
        
        // Calculate time taken
        const endTime = performance.now();
        const timeTaken = (endTime - startTime).toFixed(2);
        
        // Collect debug info
        const debugData = {
          timestamp: new Date().toISOString(),
          timeTakenMs: timeTaken,
          productId: productId,
          csvLookupId: csvLookupId || 'Not provided',
          exactMatchOnly: csvExactMatchOnly,
          resultSource: result.source || 'unknown',
          itemCount: result.data?.length || 0,
          usingFallback: result.usingFallback || false,
          browserInfo: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            platform: navigator.platform,
            hostname: window.location.hostname
          }
        };
        
        setDebugInfo(debugData);
        
        if (result.success && result.data.length > 0) {
          // Even if we got data, keep silently retrying if we're using fallback data
          const isUsingFallback = result.usingFallback || false;
          setSilentRetry(isUsingFallback);
          
          setProductData(result.data);
          setError(null);
          console.log(`Successfully loaded ${result.data.length} price items in ${timeTaken}ms from source: ${result.source || 'unknown'}`);
          
          // If using fallback data, continue to retry in the background
          if (isUsingFallback) {
            console.log('Using fallback data, will continue to retry for fresh data');
            retryTimerRef.current = setTimeout(() => {
              console.log('Background retry attempt for fresh data');
              fetchPriceData();
            }, 3000); // Retry after 3 seconds
          } else {
            // Clear any retry timers if we have real data
            if (retryTimerRef.current) {
              clearTimeout(retryTimerRef.current);
            }
          }
        } else {
          console.error('No price data found:', result);
          
          // Increment retry attempts
          const newRetryAttempts = retryAttempts + 1;
          setRetryAttempts(newRetryAttempts);
          
          // Only keep retrying silently if we haven't reached the max attempts
          if (newRetryAttempts < MAX_RETRY_ATTEMPTS) {
            // Don't show error to user, just keep retrying silently
            setSilentRetry(true);
            
            // Setup immediate retry for empty data
            retryTimerRef.current = setTimeout(() => {
              console.log(`Retrying after empty data result (attempt ${newRetryAttempts} of ${MAX_RETRY_ATTEMPTS})`);
              fetchPriceData();
            }, 2000); // Retry after 2 seconds
          } else {
            // We've reached max retries, stop silently retrying and show the "No data found" message
            console.log(`Maximum retry attempts (${MAX_RETRY_ATTEMPTS}) reached. Showing "No data found" message.`);
            setSilentRetry(false);
            setLoading(false);
          }
        }
      } catch (err) {
        console.error('Error in price comparison component:', err);
        
        // Increment retry attempts
        const newRetryAttempts = retryAttempts + 1;
        setRetryAttempts(newRetryAttempts);
        
        // Only keep retrying silently if we haven't reached the max attempts
        if (newRetryAttempts < MAX_RETRY_ATTEMPTS) {
          // Don't show error to user
          setSilentRetry(true);
          
          setDebugInfo({
            error: err.message,
            stack: err.stack
          });
          
          // Setup immediate retry for error
          retryTimerRef.current = setTimeout(() => {
            console.log(`Retrying after error (attempt ${newRetryAttempts} of ${MAX_RETRY_ATTEMPTS})`);
            fetchPriceData();
          }, 3000); // Retry after 3 seconds
        } else {
          // We've reached max retries, stop silently retrying and show the "No data found" message
          console.log(`Maximum retry attempts (${MAX_RETRY_ATTEMPTS}) reached. Showing "No data found" message.`);
          setSilentRetry(false);
          setLoading(false);
        }
      } finally {
        if (!silentRetry) {
          setLoading(false);
        }
      }
    };
    
    // Clear any existing timers before setting new ones
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
    }
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
    }
    
    // Reset retry attempts when product ID changes
    setRetryAttempts(0);
    
    fetchPriceData();
    
    // Cleanup function
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, [productId, csvLookupId, csvExactMatchOnly]); // Add csvExactMatchOnly to dependencies

  // Effect to track loading time
  useEffect(() => {
    if (loading) {
      // Reset loading duration when loading starts
      setLoadingDuration(0);
      
      // Create interval to track loading duration
      loadingTimerRef.current = setInterval(() => {
        setLoadingDuration(prev => prev + 1);
      }, 1000);
    } else {
      // Clear interval when loading stops
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }
    
    // Cleanup function
    return () => {
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
      }
    };
  }, [loading]);

  const sortProductData = () => {
    if (!productData.length) return [];
    
    return [...productData].sort((a, b) => {
      // Always sort by price (lowest to highest)
      const priceA = a.numeric_price !== undefined ? a.numeric_price : parseFloat(a.Price?.replace(/[$,]/g, '') || '0');
      const priceB = b.numeric_price !== undefined ? b.numeric_price : parseFloat(b.Price?.replace(/[$,]/g, '') || '0');
      return priceA - priceB;
    });
  };

  const formatPrice = (priceStr) => {
    if (!priceStr) return 'N/A';
    
    const price = priceStr.trim();
    if (price.startsWith('$')) return price;
    return `$${price}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    
    try {
      // Skip displaying FALLBACK text if present
      let cleanDateStr = dateStr;
      if (typeof dateStr === 'string' && dateStr.includes('FALLBACK')) {
        cleanDateStr = dateStr.replace('FALLBACK ', '');
      }
      
      // For debug purposes, log the date string
      console.log('Date string before parsing:', cleanDateStr);
      
      // Try to parse the date
      const date = new Date(cleanDateStr);
      
      // If date is invalid, return the original string 
      if (isNaN(date.getTime())) {
        console.log('Invalid date detected:', cleanDateStr);
        // Try to extract date parts if it's a custom format
        return dateStr;
      }
      
      // Log the parsed date for debugging
      console.log('Successfully parsed date:', date.toString());
      
      // Format the date properly with full date and time
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(date);
    } catch (e) {
      console.error('Error formatting date:', e);
      // Return original string on error
      return dateStr;
    }
  };

  const getRetailerLogo = (retailerName) => {
    return RETAILER_LOGOS[retailerName] || null;
  };

  const getProductTitle = () => {
    if (productDetails) return productDetails.name;
    if (!productData.length) return 'Product Price Comparison';
    return productData[0].Product || 'Product Price Comparison';
  };

  // Calculate savings compared to highest price
  const calculateSavings = () => {
    const sortedData = sortProductData();
    if (sortedData.length < 2) return null;
    
    const lowestPrice = parseFloat(sortedData[0].Price?.replace(/[$,]/g, '') || '0');
    const highestPrice = parseFloat(sortedData[sortedData.length - 1].Price?.replace(/[$,]/g, '') || '0');
    
    if (lowestPrice === 0 || highestPrice === 0) return null;
    
    const savings = highestPrice - lowestPrice;
    // Return null if there's no actual savings (prices are the same)
    if (savings <= 0) return null;
    
    const savingsPercent = Math.round((savings / highestPrice) * 100);
    
    return {
      amount: savings.toFixed(2),
      percent: savingsPercent
    };
  }

  // Function to handle copy link to clipboard
  const handleCopyLink = () => {
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl)
      .then(() => {
        setShowCopyToast(true);
        setTimeout(() => setShowCopyToast(false), 3000); // Hide toast after 3 seconds
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
      });
  };

  // Functions to handle social sharing
  const handleFacebookShare = () => {
    const currentUrl = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(getProductTitle());
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${currentUrl}&t=${title}`, 
      'facebook-share-dialog', 
      'width=800,height=600');
  };

  const handleTwitterShare = () => {
    const currentUrl = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(`Check the latest deal on the ${getProductTitle()} 🔥`);
    window.open(`https://twitter.com/intent/tweet?url=${currentUrl}&text=${title}`, 
      'twitter-share-dialog', 
      'width=800,height=600');
  };

  // Function to handle SMS sharing (mobile only)
  const handleSmsShare = () => {
    const currentUrl = window.location.href;
    const shareText = `Check out this fishing deal: ${currentUrl}`;
    window.location.href = `sms:?&body=${encodeURIComponent(shareText)}`;
  };

  // Function to handle retry button click
  const handleRetryClick = () => {
    // Reset retry attempts
    setRetryAttempts(0);
    // Start loading again
    setSilentRetry(false);
    setLoading(true);
  };

  // Function to toggle QR modal
  const toggleQrModal = () => {
    setShowQRCode(!showQRCode);
  };

  // QR code modal component
  const QrModal = () => {
    if (!showQRCode) return null;
    
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
        onClick={toggleQrModal}
      >
        <div 
          className="qr-modal"
          style={{
            background: theme.isDarkMode ? '#1c1c1c' : '#ffffff',
            borderRadius: '8px',
            padding: '16px',
            maxWidth: '300px',
            textAlign: 'center',
            position: 'relative',
            boxShadow: '0 0 12px rgba(0,0,0,0.4)',
          }}
          onClick={e => e.stopPropagation()}
        >
          <button 
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'none',
              border: 'none',
              fontSize: '16px',
              cursor: 'pointer',
              color: theme.colors.text,
            }}
            onClick={toggleQrModal}
            aria-label="Close QR code"
          >
            ❌
          </button>
          <h3 style={{ marginTop: '10px', marginBottom: '15px', color: theme.colors.text }}>
            Scan QR Code
          </h3>
          <div style={{ marginBottom: '15px' }}>
            <QRCodeSVG
              value={window.location.href}
              size={200}
              level={"M"}
              includeMargin={true}
              bgColor={theme.isDarkMode ? '#1c1c1c' : '#ffffff'}
              fgColor={theme.isDarkMode ? '#ffffff' : '#000000'}
            />
          </div>
          <p style={{ fontSize: '13px', color: theme.colors.textSecondary }}>
            Scan with your phone camera to visit this page
          </p>
        </div>
      </div>
    );
  };

  // Add debug display component
  const DebugDisplay = () => {
    if (!showDebug) return null;
    
    return (
      <div style={{
        margin: '20px 0',
        padding: '15px',
        background: '#000',
        color: '#0f0',
        fontFamily: 'monospace',
        fontSize: '12px',
        borderRadius: '4px',
        whiteSpace: 'pre-wrap',
        overflowX: 'auto'
      }}>
        <h3 style={{ color: '#0f0', margin: '0 0 10px 0' }}>Debug Information</h3>
        <div>
          <p style={{ color: '#0f0', margin: '0 0 10px 0' }}>
            Window width: {windowWidth}px | isMobile: {isMobile.toString()} | isDesktop: {isDesktop.toString()}
          </p>
          <button 
            onClick={() => {
              // Force refresh data with timestamp
              fetchProductPrices(productId).then(result => {
                setProductData(result.data || []);
                setDebugInfo({
                  ...debugInfo,
                  refreshResult: {
                    timestamp: new Date().toISOString(),
                    source: result.source,
                    itemCount: result.data?.length || 0
                  }
                });
              }).catch(err => {
                setDebugInfo({
                  ...debugInfo,
                  refreshError: err.message
                });
              });
            }}
            style={{
              background: '#333',
              color: '#0f0',
              border: '1px solid #0f0',
              padding: '5px 10px',
              marginRight: '10px',
              cursor: 'pointer'
            }}
          >
            Force Refresh
          </button>
          
          <button 
            onClick={() => {
              // Try to bypass cache with a direct fetch
              const csvUrl = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vTEy5_PFrbo0uwXTwhZqMvCAnO2GDob7JK7e2kemhP97RDw9X3TLAJEUINE0KDoGshCB9i2LV_CBIwv/pub?gid=1450571949&single=true&output=csv';
              const timestamp = Date.now();
              window.open(`${csvUrl}&_t=${timestamp}`, '_blank');
            }}
            style={{
              background: '#333',
              color: '#0f0',
              border: '1px solid #0f0',
              padding: '5px 10px',
              cursor: 'pointer'
            }}
          >
            Open CSV Directly
          </button>
        </div>
        <pre style={{ margin: '10px 0 0 0' }}>
          {JSON.stringify(debugInfo, null, 2)}
        </pre>
        <pre style={{ margin: '10px 0 0 0' }}>
          Current data items: {productData.length}
          {productData.length > 0 && (
            <>
              <br />First item: {JSON.stringify(productData[0], null, 2)}
              <br /><br />Date format details:
              <br />Raw date string: {productData[0]["Date Time Stamp"]}
              <br />Formatted date: {formatDate(productData[0]["Date Time Stamp"])}
              <br />Is valid date: {!isNaN(new Date(productData[0]["Date Time Stamp"]).getTime()) ? 'Yes' : 'No'}
            </>
          )}
        </pre>
      </div>
    );
  };

  return (
    <div className={`main-container ${isDesktop ? 'desktop' : ''}`} style={{ 
      color: theme.colors.text,
      background: theme.colors.background,
    }}>
      {/* Debug panel */}
      <DebugDisplay />
      
      {/* QR code modal */}
      <QrModal />
      
      <div className={`section ${isDesktop ? 'desktop' : ''}`} style={{
        textAlign: 'center',
        background: theme.isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)',
        borderRadius: '10px',
        boxShadow: theme.isDarkMode ? 'inset 0 0 10px rgba(0,0,0,0.2)' : 'inset 0 0 10px rgba(0,0,0,0.05)',
      }}>
        <p style={{
          fontSize: isMobile ? '14px' : '16px',
          color: theme.colors.textSecondary,
          margin: '0 0 10px 0'
        }}>
          🎯 Real-Time Price Comparison Tool
          <br />
          <span style={{ 
            fontSize: isMobile ? '12px' : '14px', 
            fontWeight: 'normal',
            opacity: 0.9
          }}>
            Instantly see the lowest verified price from trusted retailers — updated daily.
          </span>
        </p>
        
        {!loading && !error && (() => {
          const savings = calculateSavings();
          return savings && (
            <div style={{
              display: 'inline-block',
              background: theme.isDarkMode ? 'rgba(46, 204, 113, 0.15)' : 'rgba(46, 204, 113, 0.1)',
              padding: isMobile ? '6px 12px' : '8px 15px',
              borderRadius: '8px',
              margin: '5px 0 10px 0',
              border: '1px solid rgba(46, 204, 113, 0.3)'
            }}>
              <span style={{
                fontSize: isMobile ? '13px' : '15px',
                fontWeight: '600',
                color: theme.isDarkMode ? '#2ecc71' : '#27ae60'
              }}>
                {savings.percent >= 20
                  ? `Save up to $${savings.amount} (${savings.percent}%)`
                  : `Save up to $${savings.amount}`
                }
              </span>
            </div>
          );
        })()}
      </div>

      {loading || silentRetry ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px'
        }}>
          {/* Animated Loader - Pulsing Dots with Spinner */}
          <div className="loading-dots" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '15px'
          }}>
            <span style={{
              fontSize: '24px',
              fontWeight: '500',
              color: theme.colors.text,
              marginRight: '10px',
              opacity: '0.7'
            }}>
              Loading price data
            </span>
            
            {/* Spinner Animation */}
            <div className="spinner" style={{
              width: '20px',
              height: '20px',
              border: `2px solid ${theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              borderTop: `2px solid ${theme.isDarkMode ? '#ffffff' : '#333333'}`,
              borderRadius: '50%',
              animation: 'spin 1.5s linear infinite'
            }}></div>
          </div>
          
          {/* Reassurance Message - Appears after 3 seconds */}
          <div className={`reassurance-message ${loadingDuration >= 3 ? 'show' : ''}`} style={{
            opacity: loadingDuration >= 3 ? 1 : 0,
            transition: 'opacity 0.5s ease-in',
            fontSize: '14px',
            color: theme.isDarkMode ? '#aaa' : '#777',
            maxWidth: '300px',
            margin: '0 auto'
          }}>
            Still loading? Some retailers take a few extra seconds to send live pricing.
          </div>
          
          {/* Fishing Animation - Appears after 8 seconds */}
          {loadingDuration >= 8 && (
            <div className="fishing-animation" style={{ 
              marginTop: '25px',
              height: '50px',
              position: 'relative'
            }}>
              <div className="fishing-bobber"></div>
            </div>
          )}
          
          {/* CSS for Animations */}
          <style>{`
            /* Spinner Animation */
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            
            /* Pulsing Dots Animation */
            .dot {
              display: inline-block;
              width: 8px;
              height: 8px;
              border-radius: 50%;
              margin: 0 4px;
              background-color: ${theme.isDarkMode ? '#fff' : '#333'};
              opacity: 0.3;
            }
            
            .dot-1 {
              animation: pulse 1.5s infinite;
            }
            
            .dot-2 {
              animation: pulse 1.5s infinite 0.3s;
            }
            
            .dot-3 {
              animation: pulse 1.5s infinite 0.6s;
            }
            
            @keyframes pulse {
              0% { transform: scale(1); }
              50% { transform: scale(1.02); }
              100% { transform: scale(1); }
            }
            
            /* Fishing Bobber Animation */
            .fishing-bobber {
              position: absolute;
              width: 14px;
              height: 14px;
              border-radius: 50%;
              background: linear-gradient(to bottom, #ff3333 50%, #ffffff 50%);
              box-shadow: 0 1px 3px rgba(0,0,0,0.3);
              left: 50%;
              transform: translateX(-50%);
              animation: bobbing 2s ease-in-out infinite;
            }
            
            @keyframes bobbing {
              0%, 100% { top: 0; }
              50% { top: 15px; }
            }
            
            /* Fade in/out for reassurance message */
            .reassurance-message {
              opacity: 0;
              transition: opacity 0.8s ease-in;
            }
            
            .reassurance-message.show {
              opacity: 1;
            }
          `}</style>
        </div>
      ) : productData.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px'
        }}>
          {loading ? (
            <>
              {/* Animated Loader - Pulsing Dots */}
              <div className="loading-dots" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '15px'
              }}>
                <span style={{
                  fontSize: '24px',
                  fontWeight: '500',
                  color: theme.colors.text,
                  marginRight: '10px'
                }}>
                  Loading price data
                </span>
                <div className="dots-container" style={{ display: 'inline-flex' }}>
                  <div className="dot dot-1"></div>
                  <div className="dot dot-2"></div>
                  <div className="dot dot-3"></div>
                </div>
              </div>
              
              {/* Fishing Animation shown immediately as reassurance */}
              <div className="fishing-animation" style={{ 
                marginTop: '25px',
                height: '50px',
                position: 'relative'
              }}>
                <div className="fishing-bobber"></div>
              </div>
              
              <div style={{
                fontSize: '14px',
                color: theme.isDarkMode ? '#aaa' : '#777',
                maxWidth: '300px',
                margin: '20px auto 0',
                opacity: 1,
                transition: 'opacity 0.5s ease-in'
              }}>
                Some retailers take a bit longer to connect. We'll keep trying until we get the latest prices!
              </div>
            </>
          ) : (
            <>
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '20px', opacity: 0.7 }}>
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={theme.isDarkMode ? "#bbbbbb" : "#777777"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 8V12" stroke={theme.isDarkMode ? "#bbbbbb" : "#777777"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 16H12.01" stroke={theme.isDarkMode ? "#bbbbbb" : "#777777"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h3 style={{ 
                fontSize: '20px', 
                color: theme.colors.text, 
                marginBottom: '10px',
                fontWeight: '500'
              }}>
                No price data found
              </h3>
              <p style={{
                fontSize: '14px',
                color: theme.isDarkMode ? '#aaa' : '#777',
                maxWidth: '300px',
                margin: '0 auto 20px',
                textAlign: 'center'
              }}>
                We couldn't find any matching price data for this product at the moment.
              </p>
              <button 
                onClick={handleRetryClick}
                style={{
                  background: theme.colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                  <path d="M1 4V10H7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M23 20V14H17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20.49 9.00001C19.9828 7.56678 19.1209 6.2743 17.9845 5.27439C16.8482 4.27448 15.4745 3.60243 14 3.32001C12.5255 3.03759 11.0097 3.15868 9.6007 3.67143C8.19171 4.18418 6.93574 5.07117 5.96999 6.23001L1 10M23 14L18.03 17.77C17.0643 18.929 15.8083 19.816 14.3993 20.3287C12.9903 20.8415 11.4745 20.9626 10 20.68C8.52552 20.3975 7.15178 19.7255 6.01545 18.7256C4.87912 17.7257 4.01718 16.4332 3.51 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Retry
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          <div className={`price-card-container ${isDesktop ? 'desktop' : ''}`} style={{
            borderRadius: '10px',
            overflow: 'hidden',
            border: `1px solid ${theme.colors.border}`,
            boxShadow: theme.isDarkMode ? 
              '0 3px 8px rgba(0,0,0,0.2)' : 
              '0 3px 8px rgba(0,0,0,0.05)',
          }}>
            {sortProductData().map((item, index) => (
              <div 
                key={`${item.Source}-${index}`} 
                style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  padding: index === 0 
                    ? (isMobile ? '25px 12px 12px 12px' : '25px 15px 15px 15px') 
                    : (isMobile ? '12px' : '15px'),
                  borderBottom: index < sortProductData().length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                  background: index === 0 ? 
                    (theme.isDarkMode ? 'rgba(46, 204, 113, 0.15)' : 'rgba(46, 204, 113, 0.05)') : 
                    theme.colors.surface,
                  position: 'relative',
                  marginBottom: '2px',
                  marginTop: index === 0 ? '15px' : '0',
                  boxShadow: theme.isDarkMode ? 
                    'inset 0 0 0 1px rgba(255,255,255,0.05)' : 
                    'inset 0 0 0 1px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: theme.isDarkMode ? '#2a2a2a' : '#f8f8f8',
                  }
                }}
              >
                {index === 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '-10px',
                    right: isMobile ? '50%' : '15px',
                    transform: isMobile ? 'translateX(50%)' : 'none',
                    background: 'linear-gradient(90deg, #2ecc71, #27ae60)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    zIndex: 10,
                    boxShadow: '0 2px 6px rgba(46,204,113,0.3)',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '80px',
                    cursor: 'help',
                  }}
                  title="Best price based on today's verified listings"
                  >
                    LOWEST VERIFIED PRICE
                    <span style={{
                      position: 'absolute',
                      bottom: '-20px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: '10px',
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      padding: '3px 6px',
                      borderRadius: '4px',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      opacity: 0,
                      transition: 'opacity 0.2s ease',
                      pointerEvents: 'none',
                      fontWeight: 'normal',
                      width: 'max-content',
                      maxWidth: '180px'
                    }}
                    className="price-tooltip"
                    >
                      Based on today's verified listings
                    </span>
                  </div>
                )}
                
                <div style={{ 
                  flex: 1,
                  marginBottom: isMobile ? '10px' : 0,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    {getRetailerLogo(item.Source) && (
                      <img 
                        src={getRetailerLogo(item.Source)} 
                        alt={`${item.Source} logo`} 
                        style={{
                          height: isMobile ? '30px' : '40px',
                          width: isMobile ? '75px' : '90px',
                          objectFit: 'contain',
                          backgroundColor: 'white',
                          padding: '4px',
                          borderRadius: '6px',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                    )}
                    <div>
                      <h3 style={{ 
                        margin: '0 0 2px 0',
                        fontSize: isMobile ? '15px' : '16px',
                        fontWeight: '600',
                        color: theme.isDarkMode ? '#fff' : '#333'
                      }}>
                        {item.Source}
                      </h3>
                      <p style={{ 
                        margin: '0',
                        fontSize: '12px',
                        color: theme.colors.textSecondary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <span style={{ fontSize: '10px', opacity: 0.7 }}>⟳</span>
                        Updated: {formatDate(item["Date Time Stamp"])}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'row' : 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: isMobile ? '100%' : 'auto',
                  gap: '15px'
                }}>
                  <div style={{ 
                    fontSize: isMobile ? '22px' : '24px',
                    fontWeight: '700',
                    color: index === 0 ? 
                      (theme.isDarkMode ? '#2ecc71' : '#27ae60') : 
                      (theme.isDarkMode ? '#fff' : '#333')
                  }}>
                    {formatPrice(item.Price)}
                  </div>
                  
                  <a 
                    href={item["Job Link"]} 
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      padding: '8px 15px',
                      background: index === 0 ? 
                        'linear-gradient(90deg, #2ecc71, #27ae60)' : 
                        theme.colors.accent,
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      textAlign: 'center',
                      minWidth: '110px',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
                      transition: 'all 0.2s ease',
                      animation: index === 0 ? 'pulse 2s infinite' : 'none'
                    }}
                  >
                    Check Availability →
                  </a>
                </div>
              </div>
            ))}
          </div>
          
          {/* Mini affiliate disclosure - Moved above social sharing */}
          <div className={`affiliate-note ${isDesktop ? 'desktop' : ''}`} style={{
            fontSize: isMobile ? '11px' : '12px',
            lineHeight: '1.4',
            color: '#ccc',
            textAlign: 'center',
            padding: '5px',
            marginBottom: '20px'
          }}>
            <p style={{ margin: '0' }}>
              ℹ️ Note: This page contains affiliate links. I may earn a small commission if you click and buy — at no extra cost to you.
            </p>
            
            {/* Full affiliate disclosure moved here */}
            <details style={{ 
              fontSize: isMobile ? '10px' : '11px',
              marginTop: '12px'
            }}>
              <summary style={{ cursor: 'pointer', marginBottom: '0' }}><strong>Learn how this page is supported</strong></summary>
              <div style={{ padding: '5px 0 0 15px', textAlign: 'left' }}>
                <p style={{ margin: '5px 0 0 0' }}>
                  Some links on this page are affiliate links, meaning I may earn a small commission if you click through and make a purchase — at no extra cost to you. 
                  <strong> As an Amazon Associate, I earn from qualifying purchases.</strong> This helps support the content I create and allows me to keep this page updated with the latest prices from trusted retailers.
                </p>
              </div>
            </details>
          </div>
          
          {/* Social Sharing Bar - now shown on all screen sizes */}
          <div className="social-share-bar" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: '20px',
            marginBottom: '20px',
            padding: '15px',
            borderRadius: '8px',
            background: theme.isDarkMode ? '#1c1c1c' : '#f5f5f5',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            position: 'relative'
          }}>
            <p style={{
              margin: '0 0 12px 0',
              fontSize: '14px',
              fontWeight: '600',
              color: theme.isDarkMode ? '#e0e0e0' : '#333'
            }}>
              Share or Save This Deal:
            </p>
            
            <div className="share-buttons-container" style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '16px',
              width: '100%'
            }}>
              {/* Copy Link Button */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '70px',
                gap: '0'
              }}>
                <button
                  onClick={handleCopyLink}
                  aria-label="Copy link"
                  className="share-button"
                  style={{
                    backgroundColor: "#2c2c2c",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "44px",
                    height: "44px",
                    padding: 0,
                    margin: 0,
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "8px"
                  }}
                >
                  <FaLink size={18} />
                  {showCopyToast && (
                    <div className="toast-notification" style={{
                      position: "absolute",
                      bottom: "-40px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: "rgba(0,0,0,0.8)",
                      color: "#fff",
                      padding: "8px 12px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      zIndex: 10,
                    }}>
                      Link copied!
                    </div>
                  )}
                </button>
                <span style={{
                  fontSize: "11px",
                  marginTop: "0",
                  padding: 0,
                  margin: 0,
                  lineHeight: "1",
                  display: "block",
                  position: "relative",
                  top: "-4px",
                  textAlign: "center"
                }}>
                  Copy
                </span>
              </div>
              
              {/* QR Code Button */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '70px',
                gap: '0'
              }}>
                <button
                  onClick={() => setShowQRCode(!showQRCode)}
                  aria-label="Show QR Code"
                  className="share-button"
                  style={{
                    backgroundColor: "#2c2c2c",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "44px",
                    height: "44px",
                    padding: 0,
                    margin: 0,
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "8px"
                  }}
                >
                  <FaQrcode size={18} />
                </button>
                <span style={{
                  fontSize: "11px",
                  marginTop: "0",
                  padding: 0,
                  margin: 0,
                  lineHeight: "1",
                  display: "block",
                  position: "relative",
                  top: "-4px",
                  textAlign: "center"
                }}>
                  QR Code
                </span>
              </div>
              
              {/* SMS Share Button */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '70px',
                gap: '0'
              }}>
                <a
                  href={`sms:?body=${encodeURIComponent(
                    `Check out this deal: ${window.location.href}`
                  )}`}
                  aria-label="Share via SMS"
                  className="share-button"
                  style={{
                    backgroundColor: "#2c2c2c",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "44px",
                    height: "44px",
                    padding: 0,
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textDecoration: "none",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    marginBottom: "8px"
                  }}
                >
                  <FaSms size={18} />
                </a>
                <span style={{
                  fontSize: "11px",
                  marginTop: "0",
                  padding: 0,
                  margin: 0,
                  lineHeight: "1",
                  display: "block",
                  position: "relative",
                  top: "-4px",
                  textAlign: "center"
                }}>
                  Send Text
                </span>
              </div>
              
              {/* Facebook Share Button */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '70px',
                gap: '0'
              }}>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                    window.location.href
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on Facebook"
                  className="share-button"
                  style={{
                    backgroundColor: "#3b5998",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "44px",
                    height: "44px",
                    padding: 0,
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textDecoration: "none",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    marginBottom: "8px"
                  }}
                >
                  <FaFacebookF size={18} />
                </a>
                <span style={{
                  fontSize: "11px",
                  marginTop: "0",
                  padding: 0,
                  margin: 0,
                  lineHeight: "1",
                  display: "block",
                  position: "relative",
                  top: "-4px",
                  textAlign: "center"
                }}>
                  Facebook
                </span>
              </div>
              
              {/* Twitter Share Button */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '70px',
                gap: '0'
              }}>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                    window.location.href
                  )}&text=${encodeURIComponent(
                    `Check out this deal on ${productDetails?.name || "this product"}!`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on Twitter"
                  className="share-button"
                  style={{
                    backgroundColor: "#1da1f2",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "44px",
                    height: "44px",
                    padding: 0,
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textDecoration: "none",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    marginBottom: "8px"
                  }}
                >
                  <FaTwitter size={18} />
                </a>
                <span style={{
                  fontSize: "11px",
                  marginTop: "0",
                  padding: 0,
                  margin: 0,
                  lineHeight: "1",
                  display: "block",
                  position: "relative",
                  top: "-4px",
                  textAlign: "center"
                }}>
                  X
                </span>
              </div>
            </div>
            
            {/* Tip message moved inside social share bar */}
            <div style={{
              textAlign: 'center',
              color: '#aaa',
              fontSize: '13px',
              margin: '15px auto 0',
              opacity: 0.8,
              maxWidth: '80%'
            }}>
              💡 Tip: Bookmark this page and check back tomorrow — prices are updated daily.
            </div>
          </div>
          
          {/* SEO-friendly content */}
          <div className={`section ${isDesktop ? 'desktop' : ''}`} style={{
            padding: '15px',
            borderTop: `1px solid ${theme.isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
            fontSize: isMobile ? '13px' : '14px',
            lineHeight: '1.6',
            color: theme.colors.textSecondary,
            textAlign: 'justify',
          }}>
            <p>
              The Garmin LiveScope Plus LVS34 is one of the most advanced real-time sonar systems available. 
              Compare current pricing from trusted retailers like Amazon, Scheels, and more. 
              Updated daily so you get the best deal without the research.
            </p>
          </div>
          
          {/* Add the feedback form at the very bottom */}
          <PriceFeedbackForm 
            productId={productId}
            productName={getProductTitle()}
          />
          
          {/* Navigation Bar */}
          <div 
            className="nav-bar" 
            style={{
              marginTop: '40px',
              padding: '12px 0',
              textAlign: 'center',
              background: theme.isDarkMode ? 
                'linear-gradient(to bottom, #181818, #121212)' : 
                'linear-gradient(to bottom, #1a1a1a, #141414)',
              color: '#ccc',
              width: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: isMobile ? '10px' : '0'
            }}
          >
            <a 
              href="https://www.buildmyfishfinder.com" 
              style={{
                color: '#ccc',
                textDecoration: 'none',
                fontSize: isMobile ? '12px' : '14px',
                padding: isMobile ? '4px 0' : '0 10px',
                transition: 'all 0.2s ease',
                display: 'inline-block'
              }}
              aria-label="Go to Home page"
              onMouseOver={(e) => {
                e.target.style.color = '#fff';
                e.target.style.textDecoration = 'underline';
              }}
              onMouseOut={(e) => {
                e.target.style.color = '#ccc';
                e.target.style.textDecoration = 'none';
              }}
            >
              Home
            </a>
            <span style={{ color: '#666', display: isMobile ? 'none' : 'inline' }}>|</span>
            <a 
              href="https://www.buildmyfishfinder.com/custom-builder" 
              style={{
                color: '#ccc',
                textDecoration: 'none',
                fontSize: isMobile ? '12px' : '14px',
                padding: isMobile ? '4px 0' : '0 10px',
                transition: 'all 0.2s ease',
                display: 'inline-block'
              }}
              aria-label="Go to Fish Finder Builder page"
              onMouseOver={(e) => {
                e.target.style.color = '#fff';
                e.target.style.textDecoration = 'underline';
              }}
              onMouseOut={(e) => {
                e.target.style.color = '#ccc';
                e.target.style.textDecoration = 'none';
              }}
            >
              Builder
            </a>
            <span style={{ color: '#666', display: isMobile ? 'none' : 'inline' }}>|</span>
            <a 
              href="/deals" 
              style={{
                color: '#ccc',
                textDecoration: 'none',
                fontSize: isMobile ? '12px' : '14px',
                padding: isMobile ? '4px 0' : '0 10px',
                transition: 'all 0.2s ease',
                display: 'inline-block'
              }}
              aria-label="View all gear deals"
              onMouseOver={(e) => {
                e.target.style.color = '#fff';
                e.target.style.textDecoration = 'underline';
              }}
              onMouseOut={(e) => {
                e.target.style.color = '#ccc';
                e.target.style.textDecoration = 'none';
              }}
            >
              All Gear Deals
            </a>
            <span style={{ color: '#666', display: isMobile ? 'none' : 'inline' }}>|</span>
            <a 
              href="#feedback" 
              style={{
                color: '#ccc',
                textDecoration: 'none',
                fontSize: isMobile ? '12px' : '14px',
                padding: isMobile ? '4px 0' : '0 10px',
                transition: 'all 0.2s ease',
                display: 'inline-block'
              }}
              aria-label="Report an issue with this page"
              onMouseOver={(e) => {
                e.target.style.color = '#fff';
                e.target.style.textDecoration = 'underline';
              }}
              onMouseOut={(e) => {
                e.target.style.color = '#ccc';
                e.target.style.textDecoration = 'none';
              }}
              onClick={(e) => {
                e.preventDefault();
                const feedbackForm = document.getElementById('feedback');
                if (feedbackForm) {
                  feedbackForm.scrollIntoView({ behavior: 'smooth' });
                  
                  // Add highlight animation class
                  feedbackForm.classList.add('highlight-feedback');
                  
                  // Remove the class after animation completes
                  setTimeout(() => {
                    feedbackForm.classList.remove('highlight-feedback');
                  }, 3000);
                }
              }}
            >
              Report Issue
            </a>
          </div>
          
          {/* Footer Bar */}
          <div className="footer-bar" style={{
            marginTop: '12px',
            padding: '12px 0',
            textAlign: 'center',
            backgroundColor: theme.isDarkMode ? '#121212' : '#1a1a1a',
            color: 'rgba(255,255,255,0.7)',
            fontSize: isMobile ? '12px' : '14px',
            fontWeight: '300',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            © {new Date().getFullYear()} BuildMyFishFinder | A Division of Alex Timm Media, LLC
          </div>
        </>
      )}
      
      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.02); }
          100% { transform: scale(1); }
        }
        
        div[title]:hover .price-tooltip {
          opacity: 1;
        }
        
        @keyframes highlightFeedback {
          0% { box-shadow: 0 0 0 0 rgba(41, 121, 255, 0.2); }
          50% { box-shadow: 0 0 0 10px rgba(41, 121, 255, 0.4); }
          100% { box-shadow: 0 0 0 0 rgba(41, 121, 255, 0.2); }
        }
        
        .highlight-feedback {
          animation: highlightFeedback 1.5s ease-in-out 3;
          border: 2px solid #2979ff !important;
          transition: all 0.3s ease;
        }
        
        .share-button:hover {
          background-color: rgba(255, 255, 255, 0.15) !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          transform: translateY(-1px);
        }
      `}</style>
    </div>
  );
};

export default SimplePriceComparison; 